import { useParams } from "react-router";
import { useMemo, useCallback } from "react";
import dayjs from "dayjs";
import DashboardFetchWrapper from "@/components/Templates/DashboardFetchWrapper";
import TimelineLibrary from "@/components/Timeline/TimelineLibrary";
import TimelineHeaderControls from "@/components/Timeline/TimelineHeaderControls";
import { useTimelineControls } from "@/components/Timeline/useTimelineControls";
import PageHeader from "@/components/UI/PageHeader";
import useTableState from "@/hooks/useTableState";
import TableToolbar from "@/components/UI/TableToolbar/TableToolbar";
import AppliedFiltersSection from "@/components/UI/TableToolbar/Filtering/AppliedFiltersSection";
import { getColumnTitle } from "@/utils/tableHelpers";
import { prepareUnifiedDataPostRequestBody } from "@/components/UI/TableToolbar/Filtering/filtersApiUtil";
import { useFetch } from "@/hooks/FetchHook";
import Loader from "@/components/UI/Loader";
import { BASE_ENDPOINT } from "@/components/Templates/constants";
import { subjectTimelineUtils } from "@/components/Timeline/subjectTimelineUtils";
import {
  getEarliestTimelineDate,
  getLatestTimelineDate,
} from "@/components/Timeline/timelineSortUtils";
import { useScopeParams } from "@/hooks/useScopeParams";
import type {
  TimelineFilters,
  TimelineResponse,
  TimelineWithToolbarProps,
} from "./types";

export default function Timeline() {
  const { subjectId } = useParams();
  const datasetName = "timeline";
  const fullPageName = subjectId ? `${datasetName}-ssj` : datasetName;

  return (
    <div className="w-full">
      {!subjectId && (
        <div className="flex justify-between mb-4">
          <div className="flex gap-2">
            <PageHeader
              title="Timeline"
              tooltipText="View timeline of events and activities"
            />
          </div>
        </div>
      )}
      <DashboardFetchWrapper datasetName={datasetName} subjectId={subjectId}>
        {({ metadata, workspace }) => {
          return (
            <TimelineWithToolbar
              metadata={metadata}
              workspace={workspace}
              datasetName={datasetName}
              fullPageName={fullPageName}
              subjectId={subjectId}
            />
          );
        }}
      </DashboardFetchWrapper>
    </div>
  );
}

const withSubjectFilter = (
  filters: TimelineFilters,
  subjectId?: string,
): TimelineFilters => {
  if (!subjectId) return filters;
  return {
    ...filters,
    subject: [subjectId],
  };
};

const withoutSubjectFilter = (
  filters: TimelineFilters,
  subjectId?: string,
): TimelineFilters => {
  if (!subjectId) return filters;
  return Object.fromEntries(
    Object.entries(filters).filter(([key]) => key !== "subject"),
  );
};

function TimelineWithToolbar({
  metadata,
  workspace,
  datasetName,
  fullPageName,
  subjectId,
}: TimelineWithToolbarProps) {
  const { id: trialId } = useScopeParams();

  const columnsTitleCreator = useCallback(
    (column: string) => {
      const colMeta = metadata.columns.find((c) => c.field === column);
      return colMeta?.label || getColumnTitle(column);
    },
    [metadata.columns],
  );
  const { currentTableState, tableStateFunctions } = useTableState({
    initialState: {
      ...workspace.initialTableState,
      filters: withSubjectFilter(
        workspace.initialTableState.filters,
        subjectId,
      ) as any,
    },
    pageName: subjectId ? `${datasetName}-${subjectId}` : datasetName,
    isSingleSubject: Boolean(subjectId),
    trialId,
    metadataColumns: metadata.columns.map((c) => c.field),
    metadataFilterKeys: Object.keys(metadata.filters || {}),
  });

  const payload = useMemo(
    () => ({
      dataset_type: datasetName,
      ...prepareUnifiedDataPostRequestBody({
        page: 1,
        pageSize: 10000,
        global_filters: withSubjectFilter(currentTableState.filters, subjectId),
        allFiltersData: metadata.filters,
        sorting: [],
      }),
    }),
    [currentTableState.filters, metadata.filters, datasetName, subjectId],
  );

  const dataQuery = useFetch({
    endpointSuffix: `${BASE_ENDPOINT}/`,
    method: "POST",
    body: payload,
    extraQueryKey: subjectId,
  });

  const data = useMemo(
    () => (dataQuery.data as TimelineResponse | undefined)?.results ?? [],
    [dataQuery.data],
  );

  const firstEventDate = useMemo(() => getEarliestTimelineDate(data), [data]);
  const lastEventDate = useMemo(() => getLatestTimelineDate(data), [data]);

  const timelineControls = useTimelineControls({
    firstEventDate,
    initializeToFirstEvent: true,
  });

  return (
    <div className="mt-2">
      <div className="mb-4 flex flex-wrap items-center justify-end gap-2">
        <TableToolbar
          tableMetadata={metadata}
          currentTableState={currentTableState}
          guideLink="/wiki/guides/exploration-singlesubject-timeline"
          datasetName={datasetName}
          name={datasetName}
          fullPageName={fullPageName}
          tableStateFunctions={tableStateFunctions}
          workspace={{
            tableName: fullPageName,
            workspaces: workspace.workspaces,
            currentWorkspace: workspace.currentWorkspace,
            setCurrentWorkspace: workspace.setCurrentWorkspace,
            mutations: workspace.mutations,
            initialTableState: workspace.initialTableState,
          }}
          activeTabToolbarConfig={{
            showOnly: ["workspace", "filters", "columns", "download", "guide"],
          }}
          endpointSuffix="filters/data"
        />
      </div>

      <TimelineHeaderControls
        viewMode={timelineControls.viewMode}
        selectedDate={timelineControls.selectedDate}
        isTodayActive={timelineControls.isTodayActive}
        isFirstEventActive={timelineControls.isFirstEventActive}
        hasFirstEventDate={Boolean(firstEventDate)}
        minSelectableDate={firstEventDate}
        maxSelectableDate={lastEventDate}
        testIdPrefix="timeline"
        onViewModeChange={timelineControls.handleViewModeChange}
        onDateChange={timelineControls.handleDateChange}
        onTodayClick={timelineControls.handleTodayClick}
        onEarliestClick={timelineControls.handleEarliestClick}
      />

      <div className="flex items-center justify-between gap-2 mb-4">
        <AppliedFiltersSection
          filters={withoutSubjectFilter(currentTableState.filters, subjectId)}
          setFilters={tableStateFunctions.setFilters}
          filterOptions={metadata.filters}
          allFiltersData={metadata.filters}
          clearFilters={() => tableStateFunctions.setFilters({})}
          columnsTitleCreator={columnsTitleCreator}
        />
      </div>

      {dataQuery.isLoading ? (
        <div className="flex justify-center items-center h-full">
          <Loader />
        </div>
      ) : (
        <TimelineLibrary
          data={data}
          viewMode={timelineControls.viewMode}
          viewDate={timelineControls.viewDate}
          centerOnDate={timelineControls.centerOnDate}
          centerOnDateRequestId={timelineControls.centerOnDateRequestId}
          columns={currentTableState.columns}
          utils={subjectTimelineUtils}
          onHorizontalScroll={timelineControls.handleHorizontalScroll}
          onRowSelect={(item) =>
            timelineControls.handleDateChange(dayjs(item.start_date))
          }
        />
      )}
    </div>
  );
}
